import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  I9_TASK_ID,
  RIGHT_TO_WORK_TASK_DEFINITION_ID,
  RIGHT_TO_WORK_TASK_TITLE,
  SUPPORTED_COUNTRY,
} from "../../config/appConfigConstants";
import { HVH_COLORS } from "../../config/palette";
import { Stage } from "../../helpers/get-settings";
import { isSalesforceApplication } from "../../helpers/url-params";
import { getValidStatesForShowScreeningResult } from "../../helpers/utils";
import { useChecklistArb } from "../../hooks/use-arb";
import { useAppSelector } from "../../reduxStore/reduxHooks";
import { getConfigData, getFeatureFlag } from "../../reduxStore/slices/configSlice";
import {
  getRightToWorkOrI9TaskType,
  getTaskDefinitionsFromConfig,
  isTaskEnabledByAppConfig,
} from "../../utility/app-config-helper";
import { ApplicationManageData } from "../../utility/application-data";
import { CandidateData } from "../../utility/candidate-data";
import { CONTACT_US_LINK } from "../../utility/constants";
import { getCountryForDomain } from "../../utility/locale-helper";
import {
  ScreenResult,
  TRANSLATED_SCREEN_RESULT,
  getI9Status,
  getTaskScreeningResultStatus,
} from "../../utility/screening-results-helper";
import { I9Response } from "../../utility/types/common";
import { H2 } from "../typography";
import { getScreeningResultStatusFunc, getStatusIcon, i9GetScreeningResultStatusFunc } from "./screeningResultsHelper";
import { useGetApplicationManageData } from "../../hooks/apis/useGetApplicationManageData";
import { useGetCandidateData } from "../../hooks/apis/useGetCandidateData";
interface ScreeningResultsUncontrolledProps {
  stage?: Stage;
}

export interface ContingencyResponse {
  status?: string;
}

interface Result {
  label: string;
  status: string;
}

export const ScreeningResults = ({ stage }: ScreeningResultsUncontrolledProps) => {
  const { isAlumni, isEligibleToSkipAppt1 } = useAppSelector(getFeatureFlag);
  const country = getCountryForDomain();
  const applicationManageData = useGetApplicationManageData();
  const candidateData = useGetCandidateData();

  const { i9CaasStatus, bgcStatus, airBgcStatus, drugtestStatus, medicalCheckStatus, caasI9Status, caasRTWStatus } =
    useAppSelector(getConfigData);

  const { applicationId } = useParams<{
    applicationId: string;
  }>();

  const { taskTitle, isIntegratedWithCaaS } = getTaskDefinitionsFromConfig(
    RIGHT_TO_WORK_TASK_DEFINITION_ID,
    applicationId
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [i9Response, setI9Response] = useState<I9Response>();

  const bundle = useChecklistArb();
  const loadingText = bundle.getMessage("checklist-General-TaskStatus-Loading");
  const title = bundle.getMessage("Checklist-MainPage-Screening-Results");

  const requestData = async (CANDIDATE_DATA: CandidateData, APPLICATION_MANAGE_DATA: ApplicationManageData) => {
    setLoading(true);

    try {
      if (!CANDIDATE_DATA?.candidateId || !CANDIDATE_DATA?.candidateGlobalId) {
        setError(true);
      }

      const validateScreeningResultStates = async () => {
        const { applicationState } = APPLICATION_MANAGE_DATA;

        const validStates = getValidStatesForShowScreeningResult(getCountryForDomain(), !!isAlumni);

        if (!applicationState) {
          setError(true);
        } else if (!validStates.includes(applicationState)) {
          setError(true);
        }
      };

      if (!isSalesforceApplication(applicationId)) {
        validateScreeningResultStates();
      }

      if (
        isTaskEnabledByAppConfig(I9_TASK_ID, applicationId) &&
        isIntegratedWithCaaS &&
        country !== SUPPORTED_COUNTRY.MX
      ) {
        if (taskTitle == RIGHT_TO_WORK_TASK_TITLE) {
          setI9Response(caasRTWStatus);
        } else {
          setI9Response(caasI9Status);
        }
      }
    } catch (e) {
      setError(true);
    }
    if (error || !stage) {
      return null;
    }
    setLoading(false);
  };

  useEffect(() => {
    if (candidateData && applicationManageData) {
      requestData(candidateData, applicationManageData);
    }
  }, [candidateData, applicationManageData]);

  const isGenericRightToWorkTask = getRightToWorkOrI9TaskType(RIGHT_TO_WORK_TASK_DEFINITION_ID, applicationId);

  const results: Array<Result> = [
    {
      label: isGenericRightToWorkTask
        ? bundle.getMessage("Checklist-RightToWork-Task-ScreeningResultTitle")
        : bundle.getMessage("Checklist-MainPage-I9-Title"),
      status: isGenericRightToWorkTask
        ? getScreeningResultStatusFunc(i9Response, getTaskScreeningResultStatus)
        : i9GetScreeningResultStatusFunc(i9Response, getI9Status, i9CaasStatus as string, !!isEligibleToSkipAppt1),
    },
    {
      label: bundle.getMessage("Checklist-MainPage-BGC-Title"),
      status: getScreeningResultStatusFunc(bgcStatus, getTaskScreeningResultStatus),
    },
    {
      label: bundle.getMessage("Checklist-MainPage-DT-Title"),
      status: getScreeningResultStatusFunc(
        drugtestStatus === "SKIPPED" ? undefined : drugtestStatus,
        getTaskScreeningResultStatus
      ),
    },
    {
      label: bundle.getMessage("Checklist-MainPage-AIR-BGC-Title"),
      status: getScreeningResultStatusFunc(airBgcStatus, getTaskScreeningResultStatus),
    },
    {
      label: bundle.getMessage("Checklist-MainPage-MedicalCheck-Title"),
      status: getScreeningResultStatusFunc(medicalCheckStatus, getTaskScreeningResultStatus),
    },
  ];

  if (loading) {
    return (
      <Col>
        <Row padding={"1rem"} alignItems={"center"} justifyContent={"space-between"}>
          <H2>{title}</H2>
        </Row>

        <Col alignItems="center" padding={"2rem"} backgroundColor={HVH_COLORS.NEUTRAL_0}>
          <Spinner size={SpinnerSize.Small} loadingText={loadingText} showText={true} />
        </Col>
      </Col>
    );
  }

  return (
    <div id="screeningresult" data-testid="screening-results">
      <Col>
        <Row padding={"1rem"} alignItems={"center"} justifyContent={"space-between"}>
          <H2>{title}</H2>
          {results.filter((screeningResult) => screeningResult["status"] == ScreenResult.ERROR).length > 0 && (
            <Link fontSize={"T200"} target="_blank" href={CONTACT_US_LINK}>
              {bundle.getMessage("Checklist-MainPage-Contact-US")}
            </Link>
          )}
        </Row>

        <Col gridGap={"1rem"} padding={"1rem"} backgroundColor={HVH_COLORS.NEUTRAL_0}>
          {results
            .filter((result) => result.status != ScreenResult.UNDEFINED)
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((result: Result, index: number) => {
              return (
                <Row key={index} gridGap={"0.3rem"} alignItems={"center"}>
                  {getStatusIcon(result.status)}
                  <Text
                    data-testid={`screening-results-${result.label}-${result.status}`}
                    fontSize={"T200"}
                    color={
                      result.status === ScreenResult.PENDING || result.status === ScreenResult.NOT_STARTED
                        ? HVH_COLORS.NEUTRAL_70
                        : HVH_COLORS.NEUTRAL_90
                    }
                  >
                    {result.label}{" "}
                    <strong>&middot; {bundle.getMessage(TRANSLATED_SCREEN_RESULT[result.status])}</strong>
                  </Text>
                </Row>
              );
            })}
        </Col>
      </Col>
    </div>
  );
};
